import { useContext, useEffect, useState } from "react";
import Breadcrumbs from "../../shared/Breadcrumbs/Breadcrumbs";
import OwTitle from "../../shared/OwTitle/OwTitle";
import IBreadcrumbs from "../../../models/IBreadcrumbs";
import OwButton from "../../shared/OwButton/OwButton";
import OwTable from "../../shared/OwTable/OwTable";
import IOwTableCols from "../../../models/IOwTableCols";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IOnGetDiscountsDTO from "../../../models/DTO/IOnGetDiscountsDTO";
import IDiscount from "../../../models/IDiscount";
import IGetDiscountsDTO from "../../../models/DTO/IGetDiscountsDTO";
import { DiscountSidebar } from "../DiscountSidebar/DiscountSidebar";
import PriceUpchargeTypeEnum from "../../../models/PriceUpchargeTypeEnum";
import UserService from "../../../services/UserService";
import IProcessDiscountDTO from "../../../models/DTO/IProcessDiscountDTO";
import IOnProcessDiscountDTO from "../../../models/DTO/IOnProcessDiscountDTO";
import UiContext from "../../../contexts/UiContext";
import { SocketContext } from "../../../contexts/SocketContext";
import { ConfirmDeleteOverlayFn } from "../../shared/ConfirmDeleteOverlayFn/ConfirmDeleteOverlayFn";
import { OwDeleteEditActions } from "../../shared/OwDeleteEditActions/OwDeleteEditActions";

type Props = {};

export const Discounts = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const uiContext = useContext(UiContext);
    const socket = useContext(SocketContext)!;

    const breadcrumbs: IBreadcrumbs[] = [
        { title: "Dashboard", url: "/dashboard" },
        { title: "Discounts", url: "/discounts" },
    ];

    const confirmDelete = (row: IDiscount, index: number) => {
        setSourceDeleteElem(`row-${index}`);
        setRowToDelete(row);
    };

    const renderActions = (row: IDiscount, index: number) => {
        return (
            <div>
                <OwDeleteEditActions
                    isEditMode={false}
                    onTrashClickFn={() => {
                        confirmDelete(row, index);
                    }}
                    onEditClickFn={() => {
                        openSidebar(row);
                    }}
                />
            </div>
        );
    };

    const renderDiscount = (row: IDiscount) => {
        return (
            <div>
                {row.discount} {row.discount_type_id === PriceUpchargeTypeEnum.PERCENT ? "%" : ""}
            </div>
        );
    };

    const renderDate = (row: IDiscount, prop: "start_date" | "end_date") => {
        return row[prop] ? UserService.parseDate(row[prop]) : null;
    };

    const renderActive = (row: IDiscount) => {
        return row.active ? <i className={"fas fa-check text-success"} /> : <i className={"fas fa-times text-danger"} />;
    };

    const getTableCols = (): IOwTableCols[] => {
        return [
            {
                title: "",
                valueProp: "",
                renderFn: renderActions,
                renderFnEmitsRow: true,
                className: "w-5r",
            },
            {
                title: "name",
                valueProp: "name",
            },
            {
                title: "active",
                valueProp: "active",
                renderFnEmitsRow: true,
                renderFn: renderActive,
            },
            {
                title: "discount",
                valueProp: "discount",
                renderFnEmitsRow: true,
                renderFn: renderDiscount,
            },
            {
                title: "start_date",
                valueProp: "start_date",
                renderFn: (row: IDiscount) => {
                    return renderDate(row, "start_date");
                },
                renderFnEmitsRow: true,
            },
            {
                title: "end_date",
                valueProp: "end_date",
                renderFn: (row: IDiscount) => {
                    return renderDate(row, "end_date");
                },
                renderFnEmitsRow: true,
            },
        ] as IOwTableCols[];
    };

    const [tableCols, setTableCols] = useState<IOwTableCols[]>(getTableCols());
    const [dataSet, setDataSet] = useState<IDiscount[]>([]);
    const [isDataFetching, setIsDataFetching] = useState(true);
    const [isInitialData, setIsInitialData] = useState(true);
    const [offset, setOffset] = useState(0);
    const [limit, setLimit] = useState(10);
    const [sourceDeleteElem, setSourceDeleteElem] = useState<string | null>(null);
    const [rowToDelete, setRowToDelete] = useState<IDiscount | null>(null);

    const getDiscounts = () => {
        const payload: IGetDiscountsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            limit,
            offset,
        };

        socket.emit(EndpointsEnum.DiscountsGetDiscounts, payload);

        setIsDataFetching(true);
    };

    const onGetDiscounts = (data: IOnGetDiscountsDTO) => {
        setDataSet(data.discounts);
        setIsInitialData(false);
        setIsDataFetching(false);
    };

    const onProcessDiscount = (data: IOnProcessDiscountDTO) => {
        getDiscounts();
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.DiscountsOnGetDiscounts, fn: onGetDiscounts },
        { endpoint: EndpointsEnum.DiscountsOnProcessDiscount, fn: onProcessDiscount },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        getDiscounts();

        return () => {
            for (const _socket of sockets) {
                socket.off(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    const openSidebar = (discount?: IDiscount) => {
        const sidebarComponent = (
            <DiscountSidebar
                socket={socket}
                discount={discount}
                setActivityIndicator={uiContext.setActivityIndicator}
                notifyFn={uiContext.notifyFn}
                closeFn={closeSidebar}
            />
        );

        uiContext.setSidebarComponent(sidebarComponent);
        uiContext.toggleSidebar(true);
    };

    const closeSidebar = (refreshData?: boolean) => {
        uiContext.setSidebarComponent(null);
        uiContext.toggleSidebar(false);

        if (refreshData) {
            getDiscounts();
        }
    };

    const cancelDelete = () => {
        setSourceDeleteElem(null);
        setRowToDelete(null);
    };

    const commitDelete = () => {
        if (rowToDelete) {
            const payload: IProcessDiscountDTO = {
                discount: {
                    ...rowToDelete,
                },
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                deleteFlag: true,
            };

            socket.emit(EndpointsEnum.DiscountsProcessDiscount, payload);

            setRowToDelete(null);
            setSourceDeleteElem(null);
        }
    };

    return (
        <div className={"Discounts position-relative"}>
            <div className="row">
                <div className={"col-12 d-flex align-items-center justify-items-center"}>
                    <OwTitle className={"mt-3 me-3"} leftIconName={"fad fa-dollar"} title={"discounts"} />
                    <div>
                        <OwButton
                            title={"create"}
                            leftIconClass={"fas fa-plus"}
                            className={"btn-warning"}
                            fn={() => {
                                openSidebar();
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className={"row"}>
                <div className="col-12 position-relative">
                    <ConfirmDeleteOverlayFn
                        targetElem={sourceDeleteElem}
                        handleConfirm={commitDelete}
                        handleCancel={cancelDelete}
                        leftAdjustment={-12}
                        skipMainView={true}
                        nestedParentElem={"discounts-table"}
                    />

                    <OwTable
                        id={"discounts-table"}
                        columns={tableCols}
                        dataSet={dataSet}
                        userModel={userModel}
                        supplierModel={supplierModel}
                        dataFetch={isDataFetching}
                        initialData={isInitialData}
                    />
                </div>
            </div>
        </div>
    );
};
